// src/components/common/GPXUpload.jsx
import React, { useCallback, useRef, useState } from "react";

/**
 * GPXUpload
 * - Drag & drop OR click to browse
 * - Accepts .gpx (text/xml) files
 * - Shows a big file icon + filename and size when uploaded
 *
 * Props:
 *  - onUpload(file: File)  // called when file selected
 *  - onChange({ target: { value: file } }) // optional, mimics input
 *  - accept: string (default ".gpx,application/gpx+xml,text/xml")
 *  - className: string
 */
export default function GPXUpload({
  onUpload,
  onChange,
  accept = ".gpx,application/gpx+xml,text/xml",
  className = "",
}) {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback(
    (f) => {
      if (!f) return;
      // prefer the first file
      const fileObj = f[0];
      setFile(fileObj);
      onUpload && onUpload(fileObj);
      onChange && onChange({ target: { value: fileObj } });
    },
    [onUpload, onChange]
  );

  function onInputChange(e) {
    handleFiles(e.target.files);
  }

  function onDrop(e) {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  }

  function onDragOver(e) {
    e.preventDefault();
    setDragActive(true);
  }

  function onDragLeave(e) {
    e.preventDefault();
    setDragActive(false);
  }

  function openFileDialog() {
    inputRef.current && inputRef.current.click();
  }

  function removeFile() {
    setFile(null);
    onUpload && onUpload(null);
    onChange && onChange({ target: { value: null } });
  }

  return (
    <div
      className={`card dark:bg-gray-800 dark:border-gray-700 ${className}`}
      style={{ padding: 12 }}
    >
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={`w-full rounded-md border border-dashed p-6 text-center transition ${
          dragActive
            ? "border-green-500 bg-green-50 dark:bg-green-900/10"
            : "border-gray-200 dark:border-gray-600"
        } dark:bg-gray-800`}
        style={{ cursor: "pointer" }}
        onClick={openFileDialog}
        role="button"
        aria-label="Upload GPX file"
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={onInputChange}
          style={{ display: "none" }}
        />

        {!file ? (
          <>
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              className="mx-auto mb-3"
            >
              <path
                d="M12 3v10"
                stroke="#10B981"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M21 15v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4"
                stroke="#10B981"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7 10l5-5 5 5"
                stroke="#10B981"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="text-sm text-gray-600 dark:text-gray-300 mb-1">
              Drag & drop GPX here, or click to browse
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500">
              (.gpx file, track/route)
            </div>
          </>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              justifyContent: "center",
            }}
          >
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 3v10"
                stroke="#064E3B"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="dark:stroke-green-400"
              />
              <path
                d="M21 15v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4"
                stroke="#064E3B"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="dark:stroke-green-400"
              />
              <path
                d="M7 10l5-5 5 5"
                stroke="#064E3B"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="dark:stroke-green-400"
              />
            </svg>

            <div style={{ textAlign: "left" }}>
              <div style={{ fontWeight: 600 }} className="dark:text-gray-200">
                {file.name}
              </div>
              <div
                style={{ fontSize: 12 }}
                className="text-gray-500 dark:text-gray-400"
              >
                {(file.size / 1024).toFixed(1)} KB
              </div>
              <div style={{ marginTop: 8 }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile();
                  }}
                  className="text-xs text-red-500 hover:underline"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
